import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { moneyFormatter } from '@utils';
import { reportsHttp } from '@network';
import { useSettingsContext } from '@providers';
import { InfoTooltip, InitialCard } from '@components';

interface Props {
  setLoader: (num: number, value: boolean) => void;
}

export const RenewalMetrics = ({ setLoader }: Props) => {
  const cls = useStyles();

  const { admin } = useSettingsContext();
  const [numbers, setNumbers] = useState<Record<string, any>>({});

  const loadNumbers = async () => {
    setLoader(1, true);
    try {
      const nextNumbers = await reportsHttp.renewalMetricsReport();
      setNumbers(nextNumbers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(1, false);
    }
  };

  useEffect(() => {
    if (admin) {
      loadNumbers();
    }
  }, []);

  const blocks: {key: string, title: string, value?: number, amount?: number}[] = [
    {
      key: 'totalOfRenewedCredits',
      title: 'Total of renewed credits',
    },
    {
      key: 'renewalRate',
      title: 'Renewal rate per signed credit',
    },
    {
      key: 'renewalApprovalRate',
      title: 'Renewal approval rate',
    },
  ];

  return (
    <InitialCard
      left="Renewal Metrics"
      extraPadding
      autoHeight mt={22}
      rightMessage="Not affected by time filter"
      rightTooltipColor={'#042E6B'}
      rightIcon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="#F38336">
          <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 27-3 53t-10 51q-14-16-32.5-27T794-418q3-15 4.5-30.5T800-480q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93q51 0 97.5-15t85.5-42q12 17 29.5 30t37.5 20q-51 41-114.5 64T480-80Zm290-160q-21 0-35.5-14.5T720-290q0-21 14.5-35.5T770-340q21 0 35.5 14.5T820-290q0 21-14.5 35.5T770-240Zm-158-52L440-464v-216h80v184l148 148-56 56Z"/>
        </svg>
      }
    >
      <br />
      <div className={cls.list}>
        {blocks.map(block => {
          const blockNumbers = numbers ? numbers[block.key] : undefined;

          return (
            <div key={block.key} className={cls.item}>
              <div className={cls.title}>{block.title}</div>
              <div className={cls.values}>
                <div className={cls.value}>
                  {blockNumbers?.value}
                  {blockNumbers?.type === 'percent' && ' %'}
                </div>
                {blockNumbers?.amount &&
                  <div className={cls.amount}>{moneyFormatter.format(blockNumbers?.amount)}</div>
                }
              </div>
              {blockNumbers?.info && (
                <InfoTooltip title={blockNumbers.info} className={cls.info} />
              )}
            </div>
          );
        })}
      </div>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  list: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 20,
  },
  item: {
    width: 'calc(100% / 3)',
    maxWidth: 318,
    border: '1px solid var(--primary-color)',
    padding: '15px 2px',
    borderRadius: 8,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--background-color)',
    textAlign: 'center',
    position: 'relative',
  },
  title: {
    marginTop: 4,
  },
  values: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    justifyItems: 'center',
    minHeight: 80,
  },
  value: {
    fontWeight: 700,
    fontSize: 22,
    marginTop: 6,
    marginBottom: 4,
  },
  amount: {
    marginBottom: 4,
    fontWeight: 700,
    fontSize: 18,
    color: 'var(--primary-color)',
  },
  info: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
});
